'use client'

import { useState, useMemo, useCallback } from 'react'
import { formatDistanceToNow, startOfWeek } from 'date-fns'
import { motion, AnimatePresence } from 'motion/react'
import { CircleIcon, CheckIcon } from '@phosphor-icons/react'
import { useStaleReviews } from './select-stale-reviews'
import { useDismissTask, useUpdateTask } from '@/entries'
import { useDocumentView } from '@/document'
import { TEXT_STYLES, ICON_SIZES } from '@/ui/design-tokens'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTodayDateString(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function readLS(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLS(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore quota errors
  }
}

const LS_LAST_DATE = 'micro-review-last-date'
const LS_LAST_TASK = 'micro-review-last-task-id'

// ---------------------------------------------------------------------------
// MicroReviewCard
// ---------------------------------------------------------------------------

interface MicroReviewCardProps {
  activeTaskCount: number
  suppressIfWelcomeBack?: boolean
}

export function MicroReviewCard({
  activeTaskCount,
  suppressIfWelcomeBack,
}: MicroReviewCardProps) {
  const { staleTasks } = useStaleReviews()
  const dismissTask = useDismissTask()
  const updateTask = useUpdateTask()

  const [dismissed, setDismissed] = useState(false)

  // Pick a single task via round-robin
  const selectedTask = useMemo(() => {
    if (staleTasks.length === 0) return null

    const lastTaskId = readLS(LS_LAST_TASK)
    if (!lastTaskId) return staleTasks[0]

    const lastIdx = staleTasks.findIndex((t) => t.id === lastTaskId)
    if (lastIdx === -1) return staleTasks[0]

    // Pick the next one after the last reviewed
    const nextIdx = (lastIdx + 1) % staleTasks.length
    return staleTasks[nextIdx]
  }, [staleTasks])

  // Determine visibility
  const alreadyShownToday = readLS(LS_LAST_DATE) === getTodayDateString()
  const shouldShow =
    !dismissed &&
    !alreadyShownToday &&
    selectedTask !== null &&
    staleTasks.length > 0 &&
    activeTaskCount < 5 &&
    suppressIfWelcomeBack !== true

  const markShown = useCallback((taskId: string) => {
    writeLS(LS_LAST_DATE, getTodayDateString())
    writeLS(LS_LAST_TASK, taskId)
    setDismissed(true)
  }, [])

  const handleKeep = useCallback(() => {
    if (!selectedTask) return
    updateTask.mutate({ id: selectedTask.id, data: {} })
    markShown(selectedTask.id)
  }, [selectedTask, updateTask, markShown])

  const handleDone = useCallback(() => {
    if (!selectedTask) return
    // `useUpdateTask` stamps `completedAt` for a status of `done`, which is what this wrote by
    // hand before #406 routed it through the slice.
    updateTask.mutate({ id: selectedTask.id, data: { status: 'done' } })
    markShown(selectedTask.id)
  }, [selectedTask, updateTask, markShown])

  const handleLetItGo = useCallback(() => {
    if (!selectedTask) return
    // `useDismissTask`, not `useDeleteTask`: this path stamps `updatedAt` too, and the review
    // selects on it. See the write spec for why the two are separate.
    dismissTask.mutate(selectedTask.id)
    markShown(selectedTask.id)
  }, [selectedTask, dismissTask, markShown])

  const buttonClass =
    'text-muted-foreground hover:text-foreground text-sm px-2 py-1.5 min-h-[44px] rounded-md hover:bg-accent/5 transition-colors focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none cursor-pointer'

  return (
    <AnimatePresence>
      {shouldShow && selectedTask && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="border-border/30 bg-card/50 mt-6 rounded-lg border px-4 py-3"
          data-testid="micro-review-card"
        >
          <p className={TEXT_STYLES.sectionHeader}>Still on your mind?</p>

          <div className="mt-2 flex items-center gap-2">
            <CircleIcon
              className={`${ICON_SIZES.small} text-muted-foreground/40 shrink-0`}
            />
            <p className="text-foreground truncate text-sm">
              {selectedTask.title}
            </p>
          </div>

          <p className="text-muted-foreground/60 mt-0.5 pl-5 text-xs">
            Created{' '}
            {formatDistanceToNow(new Date(selectedTask.createdAt), {
              addSuffix: true,
            })}
          </p>

          <div className="mt-3 flex gap-2">
            <button type="button" onClick={handleKeep} className={buttonClass}>
              Keep
            </button>
            <button type="button" onClick={handleDone} className={buttonClass}>
              Done
            </button>
            <button
              type="button"
              onClick={handleLetItGo}
              className={buttonClass}
            >
              Let it go
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ---------------------------------------------------------------------------
// CompletionCelebration
// ---------------------------------------------------------------------------

export function CompletionCelebration() {
  const doc = useDocumentView()

  const count = useMemo(() => {
    if (!doc) return 0
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
    let c = 0
    for (const entry of Object.values(doc.entries)) {
      if (entry.type !== 'task') continue
      if (entry.archivedAt) continue
      const metadata = entry.metadata as {
        status?: string
        completedAt?: string
      }
      if (metadata.status !== 'done' || !metadata.completedAt) continue
      if (new Date(metadata.completedAt) >= weekStart) c++
    }
    return c
  }, [doc])

  if (count === 0) return null

  return (
    <p
      className="text-muted-foreground/60 mt-4 flex items-center gap-1.5 px-1 text-xs"
      data-testid="completion-celebration"
    >
      <CheckIcon className={`${ICON_SIZES.small} shrink-0`} />
      {count} {count === 1 ? 'task' : 'tasks'} completed this week
    </p>
  )
}
